import React, { useState, useEffect, useRef } from 'react'
import { string, node, func } from 'prop-types'
import { debounce } from 'throttle-debounce'

import MTCaptcha from 'Utils/MTCaptcha'
import getUtmCookies from 'Utils/utmCookies'
import InputField from 'Pods/Shared/NewInputField'
import { run, RuleRunner } from 'Pods/Shared/RuleRunner'
import { required, isEmail, isUsername } from 'Pods/Shared/Rules/Rules'
import ReactPasswordStrength from 'Pods/Shared/ReactPasswordStrength'
import Icon from 'Pods/Shared/Icon/Icon'

const fieldValidations = [
  RuleRunner('username', 'Username', required, isUsername),
  RuleRunner('email', 'Email', required, isEmail),
  RuleRunner('password', 'Password', required),
]

function GuestRegister({
  email: initialEmail,
  checkbox,
  updateEmail,
  source = null,
  captchaId = '',
}) {
  const ReactPasswordStrengthRef = useRef()

  const [fields, setFields] = useState({
    email: initialEmail,
    username: '',
    password: '',
    gdpr: 'undecided',
    availabilityNotifications: {
      username: { message: null, type: 'notification' },
      email: { message: null, type: 'notification' },
    },
    validationErrors: {},
    showErrors: false,
  })

  const registrationCaptchaEnabled = window.gon.features && window.gon.features.rcgcaptcha

  useEffect(() => {
    setFields({ ...fields, validationErrors: run(fields, fieldValidations) })
  }, [])

  function errorFor(field) {
    return fields.validationErrors[field] || ''
  }

  function handleInputChange(e) {
    const newFields = { ...fields, [e.target.name]: e.target.value }

    if (e.target.name === 'username' && !newFields.validationErrors.username) {
      debounce(200, checkAccountAvailability(newFields.username, 'username', newFields))
    } else if (e.target.name === 'email' && !newFields.validationErrors.email) {
      debounce(200, checkAccountAvailability(newFields.email, 'email', newFields))
    }

    setFields({ ...newFields, validationErrors: run(newFields, fieldValidations) })

    if (e.target.name === 'email') {
      updateEmail(newFields.email)
    }
  }

  function handlePasswordChange(state) {
    const newFields = { ...fields, password: state.password }
    const newValidationErrors = run(newFields, fieldValidations)

    if (!state.isValid) {
      newValidationErrors.password = 'Is too weak'
    } else {
      delete newValidationErrors.password
    }

    setFields({ ...newFields, validationErrors: run(newFields, fieldValidations) })
  }

  function checkAccountAvailability(input, type, newFields) {
    axios.get(`/api/internal/users/${type}/availability`, { params: { [type]: input } })
      .then((response) => onCheckAccountAvailabilitySuccess(response, type, newFields))
      .catch((error) => { })
  }

  function onCheckAccountAvailabilitySuccess(response, type, newFields) {
    const capitalizedType = type[0].toUpperCase() + type.substring(1)
    let message
    let messageType

    if (response.data.success) {
      message = `${capitalizedType} is available`
      messageType = 'notification'
    } else {
      message = `${capitalizedType} is taken by another account`
      messageType = 'error'
    }

    setFields({
      ...newFields,
      availabilityNotifications: {
        ...newFields.availabilityNotifications,
        [type]: {
          message, type: messageType,
        },
      },
    })
  }

  function handleSubmit(e) {
    e.preventDefault()

    setFields({ ...fields, showErrors: true })

    const validationErrorsPresence = Object.getOwnPropertyNames(fields.validationErrors).length !== 0

    const availabilityErrorsPresence = Object.values(
      fields.availabilityNotifications
    ).filter(n => n.type === 'error').length !== 0

    if (validationErrorsPresence || availabilityErrorsPresence) {
      return null
    }

    const {
      referrer,
      raw_utm_string,
    } = getUtmCookies()

    const data = {
      new_user: {
        username: fields.username,
        email: fields.email,
        password: fields.password,
        gdpr_consent: fields.gdpr,
        source,
      },
      from_checkout: true,
      utm_statistics: {
        referrer,
        raw_utm_string,
      },
      ...(registrationCaptchaEnabled && { MTCaptchaToken: window.mtcaptcha.getVerifiedToken(captchaId) }),
    }

    axios.post('/users', data)
      .then(onSubmitSuccess)
      .catch(onSubmitFailure)
  }

  function onSubmitSuccess(response) {
    if (typeof response.data === 'string') {
      // Security tool asks for verification.
      document.write(response.data)
    } else {
      window.location = '/cg/checkout/payment'
    }
  }

  function onSubmitFailure(xhr) {
    if (registrationCaptchaEnabled) {
      window.mtcaptcha.resetUI(captchaId)
    }
    App.events.publish('app:notification:flash:error', xhr.response.data.errors)
  }

  function renderPasswordError() {
    let errorText

    switch (fields.validationErrors.password) {
      case 'Password is required':
        errorText = 'Password is required'
        break;
      case 'Is too weak':
        if (fields.password.length <= 5) {
          errorText = 'Please enter a longer password to continue'
        } else {
          errorText = 'Please enter a stronger password to continue'
        }
        break;
      default:
        errorText = ''
        break;
    }

    if (errorText.length === 0) { return null }

    return (
      <div className="cgt-input__error-message">
        <span className="text">{errorText}</span>
      </div>
    )
  }

  return (
    <div className="user-auth-container">
      <div className="row">
        <div className="col-12">
          <form onSubmit={handleSubmit}>
            <InputField
              label="Email"
              id="email-guest"
              name="email"
              errorText={errorFor('email')}
              availabilityNotification={fields.availabilityNotifications.email}
              showError={fields.showErrors}
              value={fields.email}
              onChange={handleInputChange}
            />
            {checkbox}
            <div className={`cgt-input ${fields.showErrors && fields.validationErrors.password ? 'cgt-input--error' : ''}`}>
              <label htmlFor="password">Password</label>
              <ReactPasswordStrength
                ref={ReactPasswordStrengthRef}
                minLength={6}
                inputProps={{
                  id: 'password-guest',
                  name: 'password',
                }}
                changeCallback={handlePasswordChange}
              />
              {fields.showErrors && (
                renderPasswordError()
              )}
            </div>
            {registrationCaptchaEnabled && <MTCaptcha captchaId={captchaId} />}
            <button
              className="cgt-button cgt-button--primary cgt-button--icon cgt-button--width-expand"
              type="submit"
            >
              <span>Continue to payment</span>
              <Icon type={['far', 'angle-right']} />
            </button>
          </form>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="cgt-legacy-text-tiny">
            By continuing you confirm that you have read and accept
            our <a href="/pages/terms-and-conditions" target="_blank" className="cgt-legacy-link">Terms of Use</a>
          </div>
        </div>
      </div>
    </div>
  )
}

GuestRegister.propTypes = {
  email: string.isRequired,
  checkbox: node.isRequired,
  updateEmail: func.isRequired,
  source: string,
  captchaId: string,
}

export default GuestRegister
